/*
 * Collection "Load more" — appends the next page of products in place instead
 * of navigating, and puts them back when the visitor returns.
 *
 * Progressive enhancement: the button (snippets/load-more.liquid) is the real
 * "next page" link, so with no JavaScript, or for a crawler, it is ordinary
 * pagination. Here it is intercepted: the next page is fetched through the
 * Section Rendering API, its cards are appended to the grid, and the button is
 * updated in place with the page after that (or hidden on the last page).
 *
 * Coming back: someone who loads three pages, opens a product and presses Back
 * should land where they were, not at page one with everything gone. When the
 * browser restores the page from its back/forward cache that happens on its
 * own — nothing here runs. When it doesn't (the page is re-fetched), the pages
 * loaded and the scroll position live in this history entry's state, so on
 * load the extra pages are fetched again, appended, and the scroll put back.
 * The URL itself is left alone: it stays a clean link to the listing.
 *
 * Accessibility: the button announces busy while loading, a live region
 * reports "Showing X of Y products", and focus moves to the first new product
 * so keyboard and screen-reader users carry on from where the new items start
 * (after a mouse click that focus draws no ring). Failures fall back to the
 * link's own navigation, so nobody is stranded.
 *
 * Plays with the filter panel: a filter change re-renders the results region
 * and dispatches `filters:rendered`, which resets the loaded-page state.
 */

const LOADING_LABEL = 'Loading…';

const init = (control) => {
  const section = control.closest('.shopify-section');
  if (!section || section.dataset.loadMoreReady) return;
  section.dataset.loadMoreReady = '1';

  const sectionId = control.dataset.sectionId;
  // Products that come before the first rendered page — non-zero when someone
  // arrives on a middle page through a real pagination link.
  const offset = parseInt(control.dataset.offset, 10) || 0;
  const $ = (sel) => section.querySelector(sel);

  /* ---------- history state ---------- */

  const state = () => (window.history.state && window.history.state.loadMore) || null;
  const setState = (patch) => {
    window.history.replaceState(
      { ...(window.history.state || {}), loadMore: { ...(state() || {}), ...patch } },
      ''
    );
  };
  const clearState = () => {
    if (!state()) return;
    window.history.replaceState({ ...(window.history.state || {}), loadMore: null }, '');
  };

  /* ---------- fetching + DOM ---------- */

  const fetchPage = async (href) => {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    const response = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Load more failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  /* Move a fetched page's cards into the grid and advance the button. The
     control is updated in place rather than replaced, so a focused button
     stays focused and the filter module's region swap is the only thing that
     ever replaces it. */
  const append = (doc) => {
    const grid = $('.listing-grid');
    const items = [...doc.querySelectorAll('.listing-grid__item')];
    items.forEach((item) => {
      // The server eager-loads each page's first row; appended below the fold
      // that would just compete with what's on screen.
      item.querySelectorAll('img').forEach((img) => {
        img.loading = 'lazy';
        img.removeAttribute('fetchpriority');
      });
      grid.appendChild(item);
    });

    const ctl = $('[data-load-more]');
    const fresh = doc.querySelector('[data-load-more]');
    const freshButton = fresh && fresh.querySelector('[data-load-more-button]');
    if (ctl && freshButton) {
      ctl.dataset.page = fresh.dataset.page;
      $('[data-load-more-button]').setAttribute('href', freshButton.getAttribute('href'));
      ctl.hidden = false;
    } else if (ctl) {
      ctl.hidden = true; // that was the last page
    }
    return items;
  };

  const announce = () => {
    const status = $('[data-filter-status]');
    const ctl = $('[data-load-more]');
    if (!status || !ctl) return;
    const shown = offset + section.querySelectorAll('.listing-grid__item').length;
    const total = parseInt(ctl.dataset.total, 10);
    status.textContent =
      Number.isFinite(total) && shown >= total
        ? `Showing all ${total} products`
        : `Showing ${shown} of ${total} products`;
  };

  const setBusy = (busy) => {
    const button = $('[data-load-more-button]');
    if (!button) return;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (busy) {
      button.dataset.label = button.textContent;
      button.textContent = LOADING_LABEL;
    } else if (button.dataset.label) {
      button.textContent = button.dataset.label;
    }
  };

  /* ---------- load more ---------- */

  section.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-load-more-button]');
    if (!button) return;
    event.preventDefault(); // before the busy check: a second press mustn't follow the link
    if (button.getAttribute('aria-busy') === 'true') return;

    const ctl = $('[data-load-more]');
    const nextPage = parseInt(ctl.dataset.page, 10) + 1;
    setBusy(true);
    let items;
    try {
      items = append(await fetchPage(button.href));
    } catch (error) {
      window.location.href = button.href; // the link still works as a link
      return;
    }
    setBusy(false);
    setState({ page: nextPage, scrollY: window.scrollY });
    announce();
    const first = items[0] && items[0].querySelector('a, button');
    if (first) first.focus({ preventScroll: true });
  });

  /* ---------- put it back on return ---------- */

  const restore = async () => {
    const saved = state();
    if (!saved || !(saved.page > 1)) return;
    const button = $('[data-load-more-button]');
    if (!button) {
      clearState(); // nothing to load more of any more
      return;
    }
    setBusy(true);
    try {
      // Pages 2..N together, appended in order.
      const base = new URL(button.href, window.location.origin);
      const docs = await Promise.all(
        Array.from({ length: saved.page - 1 }, (_, i) => {
          const url = new URL(base);
          url.searchParams.set('page', String(i + 2));
          return fetchPage(url.href);
        })
      );
      docs.forEach(append);
      announce();
      if (typeof saved.scrollY === 'number') window.scrollTo({ top: saved.scrollY, behavior: 'auto' });
    } catch (error) {
      clearState(); // leave page one in place rather than half a restore
    }
    setBusy(false);
  };

  // Keep the scroll position current for the return trip. pagehide (unlike
  // unload) doesn't disqualify the page from the back/forward cache.
  window.addEventListener('pagehide', () => {
    if (state()) setState({ scrollY: window.scrollY });
  });

  // A filter change re-renders the region from page one.
  section.addEventListener('filters:rendered', clearState);

  restore();
};

const initAll = () => document.querySelectorAll('[data-load-more]').forEach(init);

if (document.readyState !== 'loading') initAll();
else document.addEventListener('DOMContentLoaded', initAll);

document.addEventListener('shopify:section:load', (event) => {
  const control = event.target.querySelector('[data-load-more]');
  if (control) init(control);
});
